/**
 * R268ii · Feb 2026 — User Preferences hook (cross-device UI state).
 *
 * Loads the current user's preferences blob once on mount, exposes:
 *   • `prefs`       — the current in-memory blob (deep object)
 *   • `getPref(path, fallback)` — dot-path getter (e.g. "sidebar.finance.collapsed")
 *   • `setPref(patchObj)`       — deep-merge, updates state + debounce-PATCHes server
 *   • `clearKey(key)`           — remove a top-level key
 *
 * Falls back to `localStorage` when the API is unreachable, so the
 * UI never blocks on a network hiccup. Once the API recovers, the
 * server copy wins on the next mount.
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const CTX = createContext(null);
const LS_KEY = "user_prefs_cache_v1";

function deepMerge(dst, src) {
  const out = { ...(dst || {}) };
  for (const [k, v] of Object.entries(src || {})) {
    if (v && typeof v === "object" && !Array.isArray(v) &&
          dst?.[k] && typeof dst[k] === "object" && !Array.isArray(dst[k])) {
      out[k] = deepMerge(dst[k], v);
    } else {
      out[k] = v;
    }
  }
  return out;
}

function getByPath(obj, path, fallback) {
  const parts = String(path || "").split(".").filter(Boolean);
  let cur = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== "object") return fallback;
    cur = cur[p];
  }
  return cur === undefined ? fallback : cur;
}

export function UserPreferencesProvider({ children }) {
  const [prefs, setPrefs] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); }
    catch { return {}; }
  });
  const [ready, setReady] = useState(false);
  const pendingRef = useRef({});
  const flushTimer = useRef(null);

  // Load once on mount (server wins over localStorage cache).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/me/preferences");
        if (!cancelled) {
          const serverPrefs = r.data?.prefs || {};
          setPrefs(serverPrefs);
          try { localStorage.setItem(LS_KEY, JSON.stringify(serverPrefs)); } catch {}
        }
      } catch (e) { /* keep localStorage copy on failure */ }
      finally { if (!cancelled) setReady(true); }
    })();
    return () => { cancelled = true; };
  }, []);

  const flush = useCallback(async () => {
    const patch = pendingRef.current;
    pendingRef.current = {};
    if (!Object.keys(patch).length) return;
    try {
      const r = await api.patch("/me/preferences", patch);
      if (r.data?.prefs) {
        setPrefs(r.data.prefs);
        try { localStorage.setItem(LS_KEY, JSON.stringify(r.data.prefs)); } catch {}
      }
    } catch (e) {
      // network fail — re-merge patch so we retry next flush; but never
      // lose the user's intent.
      pendingRef.current = deepMerge(patch, pendingRef.current);
    }
  }, []);

  const setPref = useCallback((patch) => {
    if (!patch || typeof patch !== "object") return;
    // Optimistic local update.
    setPrefs((cur) => {
      const next = deepMerge(cur, patch);
      try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
    pendingRef.current = deepMerge(pendingRef.current, patch);
    if (flushTimer.current) clearTimeout(flushTimer.current);
    flushTimer.current = setTimeout(flush, 400);
  }, [flush]);

  const getPref = useCallback((path, fallback) => getByPath(prefs, path, fallback), [prefs]);

  const clearKey = useCallback(async (key) => {
    try { await api.delete(`/me/preferences/${encodeURIComponent(key)}`); } catch {}
    setPrefs((cur) => {
      const next = { ...cur };
      delete next[key];
      try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  return (
    <CTX.Provider value={{ prefs, ready, getPref, setPref, clearKey }}>
      {children}
    </CTX.Provider>
  );
}

export function useUserPreferences() {
  const v = useContext(CTX);
  // Safe fallback so components can call this even outside the provider
  // (test harnesses, isolated dev pages). Behaves as ephemeral in-memory.
  if (!v) {
    return {
      prefs: {}, ready: true,
      getPref: (_p, fb) => fb, setPref: () => {}, clearKey: () => {},
    };
  }
  return v;
}
